import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Input, IconButton, Typography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import debounce from 'lodash/debounce'

interface SocialLinkRowProps {
  classes?: StyleClasses<typeof useStyles>
  label: string
  /** character count of the longest label among sibling rows, so every input lines up */
  labelWidth: number
  value: string
  onChange: (value: string) => void
  onRemove: () => void
}

// Mirrors StringOption's root/label/input classes exactly (same flex row, same font
// sizes/paddings) so a link row measures the same height as every other options row —
// only the trailing delete icon is added. The delete button reuses ButtonSelectOption's
// square `button` styling (borderRadius: 0 turns MUI's default circular hover/ripple
// overlay into the same square highlight the S/M/L and alignment buttons show).
const useStyles = makeStyles<Theme, SocialLinkRowProps>(
  (theme: Theme) => {
    return {
      root: {
        display: 'flex',
        alignItems: 'center'
      },
      label: (props) => ({
        flex: '0 0 auto',
        minWidth: `${props.labelWidth}ch`,
        fontSize: '.9rem',
        color: theme.palette.grey[800]
      }),
      // targets Input's outer wrapper div: it's the flex item in `root`, so it (not the
      // input box itself) needs `flex: 1` — and `minWidth: 0` so it can actually shrink
      // below its content's natural size instead of overflowing past the delete button.
      inputWrapper: {
        flex: 1,
        minWidth: 0,
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(1)
      },
      inputBox: {
        width: '100%'
      },
      input: {
        padding: '2px 6px'
      },
      button: {
        borderRadius: 0,
        height: 26,
        width: 26,
        flexShrink: 0,
        color: theme.palette.grey[800],
        '& svg': {
          fontSize: '1rem',
          color: theme.palette.grey[800]
        },
        '&:hover': {
          color: theme.palette.grey[800]
        }
      }
    }
  },
  { name: 'SocialLinkRow' }
)

export default function SocialLinkRow(props: SocialLinkRowProps) {
  const classes = useStyles(props)
  const { label, value: propsValue, onChange, onRemove } = props
  const [value, setValue] = useState(propsValue)

  // `links` is an index-keyed array (no stable id per row) — removing a row above this one
  // shifts every row below it into a reused component instance at the same key/position, with
  // a *different* logical link's `value`. Resync from props so this row doesn't keep showing
  // whatever the previous occupant of this slot had typed.
  useEffect(() => {
    setValue(propsValue)
  }, [propsValue])

  // Debounced once for the component's lifetime (see below for why), so it must never close
  // over `onChange` directly — a sibling row being added/removed re-renders this row with a new
  // `onChange` closure (it captures the current array index), and a stale-onChange debounce
  // would commit a value to the wrong link. Read the latest one from a ref instead.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const handleOnChange = useMemo(
    () =>
      debounce((newValue: string) => {
        onChangeRef.current(newValue)
      }, 400),
    []
  )

  useEffect(() => () => handleOnChange.cancel(), [handleOnChange])

  const batchOnChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setValue(ev.target.value)
    handleOnChange(ev.target.value)
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.label} variant={'h5'}>
        {label}
      </Typography>
      <Input
        classes={{ wrapper: classes.inputWrapper, root: classes.inputBox, input: classes.input }}
        value={value}
        onChange={batchOnChange}
      />
      <IconButton classes={{ root: classes.button }} onClick={onRemove}>
        <DeleteOutlineIcon />
      </IconButton>
    </div>
  )
}
